import queryString from "../queryString/index";
const PLACEHOLDER_ORIGIN = "http://placeholder.placeholder";
const identity = x => x;
export const parseUrl = url => {
  if (typeof url === "object") {
    const base = url.url;
    const params = url.params;
    const ret = [base];
    if (params) {
      ret.push(queryString.stringify(params));
    }
    return ret.join("?");
  }
  return url;
};
/**
 * An `Error` created by `RawApiClient`
 */
export class HttpError extends Error {
  constructor(m) {
    super(m);
    // Set the prototype explicitly because when this is transpiled to ES5, it
    // uses a class constructor pattern that won't properly extend the builtin
    // Error class. See:
    // https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, HttpError.prototype);
  }
}
function hasGetTotalCountFromResponse(options) {
  return options.getTotalCountFromResponse !== undefined;
}
/**
 * Create a new API client for accessing one of PlanGrid's APIs
 *
 * The API client has request and response transformers which modify the request
 * or response before either sending it out over the wire (for requests), or
 * resolving the promise (for responses).
 *
 * Request and response transformers are applied in order, with the output of
 * one being passed to the input of the next.
 *
 * @typeparam R - the type of the response after applying all response
 *   transformers
 */
export default class RawApiClient {
  /**
   * Makes a client to connect to an API
   *
   * @param options {object}
   *   - requestTransformers {array} An array of functions that take
   *     `{ url, init }` and return a transformed version of `{ url, init }`.
   *     These are executed in a chain, so the output of one is sent to the
   *     input of the next until finally the `url` and `init` values are sent to
   *     `fetch`.
   *   - responseTransformers {array} An array of objects with two functions:
   *     `then` and `catch`.  These response transformers are chained unto the
   *     promise returned from `fetch` in the order that they appear in this
   *     array. The output promise from one is sent into the next, and the last
   *     promise is returned to the original caller.
   */
  constructor(options) {
    this.requestTransformers = options.requestTransformers || [];
    this.responseTransformers = options.responseTransformers || [];
  }
  /**
   * Makes a GET request
   *
   * @param url The url to use
   * @param init optional `Init` object (see `fetch` documentation)
   */
  get(url, init) {
    return this.fetch(parseUrl(url), init);
  }
  /**
   * Makes a POST request
   *
   * @param url The url to use
   * @param requestBody The request body. This is JSON stringified before
   *    sending over the wire.
   * @param init optional `Init` object (see `fetch` documentation)
   */
  post(url, requestBody, init) {
    const nextInit = init || {};
    nextInit.body = JSON.stringify(requestBody);
    nextInit.method = "POST";
    const {
      url: finalUrl,
      init: finalInit,
    } = addJsonContentTypeRequestTransformer({
      url: parseUrl(url),
      init: nextInit,
    });
    return this.fetch(finalUrl, finalInit);
  }
  /**
   * Makes a PUT request
   *
   * @param url The url to use
   * @param requestBody The request body. This is JSON stringified before
   *    sending over the wire.
   * @param init optional `Init` object (see `fetch` documentation)
   */
  put(url, requestBody, init) {
    const nextInit = init || {};
    nextInit.body = JSON.stringify(requestBody);
    nextInit.method = "PUT";
    const {
      url: finalUrl,
      init: finalInit,
    } = addJsonContentTypeRequestTransformer({
      url: parseUrl(url),
      init: nextInit,
    });
    return this.fetch(finalUrl, finalInit);
  }
  /**
   * Makes a PATCH request
   *
   * @param url The url to use
   * @param requestBody The request body. This is JSON stringified before
   *    sending over the wire.
   * @param init optional `Init` object (see `fetch` documentation)
   */
  patch(url, requestBody, init) {
    const nextInit = init || {};
    nextInit.body = JSON.stringify(requestBody);
    nextInit.method = "PATCH";
    const {
      url: finalUrl,
      init: finalInit,
    } = addJsonContentTypeRequestTransformer({
      url: parseUrl(url),
      init: nextInit,
    });
    return this.fetch(finalUrl, finalInit);
  }
  /**
   * Makes a PUT request
   *
   * @param url The url to use
   * @param requestBody The request body. This is JSON stringified before
   *    sending over the wire.
   * @param init optional `Init` object (see `fetch` documentation)
   */
  delete(url, requestBody, init) {
    const nextInit = init || {};
    if (requestBody) {
      nextInit.body = JSON.stringify(requestBody);
    }
    nextInit.method = "DELETE";
    return this.fetch(parseUrl(url), nextInit);
  }
  async fetch(url, init) {
    const request = await this.requestTransformers.reduce(
      (requestArgumentsPromise, transformer) =>
        requestArgumentsPromise.then(requestArguments =>
          Promise.resolve(transformer(requestArguments))
        ),
      Promise.resolve({ url, init })
    );
    const initialPromise = fetch(request.url, request.init);
    return this.responseTransformers.reduce(
      (promise, transformer) =>
        promise.then(transformer.then, transformer.catch),
      initialPromise
    );
  }
  /**
   * Fetches all pages
   *
   * @param url Base URL to use for fetching. This *may* have query params but
   *    *may not* have either the `skipParam` or the `limitParam` from `options`
   *    set.
   * @param options
   * @param init
   * @param requestBody An optional body that is used only the method in the init param is "POST"
   */
  async getAll(url, options, init, requestBody) {
    const {
      skipParam = "skip",
      limitParam = "limit",
      getPageFromResponse = identity,
      perPage = 50,
      maxConcurrent = 4,
      onProgress = () => {},
    } = options;
    const urlString = parseUrl(url);
    // HACK: use a fake origin for root relative urls so url can be parsed
    const parsedUrl = new URL(urlString, PLACEHOLDER_ORIGIN);
    const origin =
      parsedUrl.origin !== PLACEHOLDER_ORIGIN ? parsedUrl.origin : "";
    const params = new URLSearchParams(parsedUrl.search);
    if (params.has(skipParam) || params.has(limitParam)) {
      throw new HttpError(
        "Cannot specify a URL that includes skipParam or limitParam"
      );
    }
    params.set(limitParam, `${perPage}`);
    const fetchPage = pageIndex => {
      params.set(skipParam, `${pageIndex * perPage}`);
      if (init && init.method === "POST") {
        return this.post(
          `${origin}${parsedUrl.pathname}?${params.toString()}`,
          requestBody,
          init
        );
      }
      return this.get(
        `${origin}${parsedUrl.pathname}?${params.toString()}`,
        init
      );
    };
    let firstPage;
    let totalCount;
    if (hasGetTotalCountFromResponse(options)) {
      firstPage = await fetchPage(0);
      totalCount = options.getTotalCountFromResponse(firstPage);
    } else {
      totalCount = options.totalCount;
    }
    if (totalCount === 0) {
      return Promise.resolve([]);
    }
    const pageCount = Math.ceil(totalCount / perPage);
    const receivedPages = new Array(pageCount).fill(false);
    return new Promise((resolve, reject) => {
      let next = 0;
      let hasError = false;
      const handlePageReceived = (pageIndex, pageItems) => {
        receivedPages[pageIndex] = pageItems;
        const countReceived = receivedPages.reduce(
          (acc, page) => acc + (page ? 1 : 0),
          0
        );
        onProgress(countReceived / pageCount);
        if (countReceived === pageCount) {
          resolve(receivedPages.reduce((a, b) => a.concat(b), []));
        }
      };
      const sendNext = () => {
        if (next < pageCount && !hasError) {
          const pageIndex = next;
          next += 1;
          // If this is the first page, it might have already been fetched
          if (pageIndex === 0 && firstPage) {
            handlePageReceived(pageIndex, getPageFromResponse(firstPage));
            sendNext();
            return;
          }
          // Not the first page, so actually fetch it
          fetchPage(pageIndex)
            .then(response => {
              handlePageReceived(pageIndex, getPageFromResponse(response));
              sendNext();
            })
            .catch(err => {
              hasError = true;
              reject(err);
            });
        }
      };
      // This fetches the first `maxConcurrent` number of pages concurrently
      for (let i = 0; i < maxConcurrent && i < pageCount; i++) sendNext();
    });
  }
}
// Adds { credentials : "include" } to the request
export const includeCredentialsRequestTransformer = ({ url, init }) => ({
  url,
  init: Object.assign({}, init, { credentials: "include" }),
});
export const buildServiceUrlRequestTransformer = serviceUrl => ({
  url,
  init,
}) => ({
  url: new URL(url, serviceUrl).href,
  init,
});
function isHeaders(headers) {
  return headers.set !== undefined;
}
export const buildRequestHeadersTransformer = headers => request => {
  const headersInstance = new Headers(headers);
  const { url, init } = request;
  if (init && init.headers) {
    if (isHeaders(init.headers)) {
      init.headers.forEach((fieldValue, fieldName) =>
        headersInstance.set(fieldName, fieldValue)
      );
    } else {
      Object.entries(init.headers).forEach(([fieldName, fieldValue]) => {
        headersInstance.set(fieldName, fieldValue);
      });
    }
  }
  return {
    url,
    init: Object.assign({}, init, { headers: headersInstance }),
  };
};
// Adds a header Content-Type: application/json to the request
export const addJsonContentTypeRequestTransformer = buildRequestHeadersTransformer(
  // eslint-disable-line max-len
  {
    "Content-Type": "application/json",
  }
);
// If the response status code is not 200-level, reject the promise instead of resolving it
export const throwNon200ResponseTransformer = {
  then: function throwNon200Response(response) {
    if (!response.ok) {
      const error = new HttpError(
        `HTTP Error: ${response.status} ${response.statusText}`
      );
      error.response = response;
      return Promise.reject(error);
    } else {
      return Promise.resolve(response);
    }
  },
};
const makeJsonParseError = (jsonParseError, response) => {
  let message;
  if (response.ok) {
    message = "Response is not valid JSON";
  } else {
    message =
      `HTTP Error: ${response.status} ${response.statusText}, also ` +
      "response is not valid JSON";
  }
  const error = new HttpError(message);
  error.response = response;
  error.jsonParseError = jsonParseError;
  return error;
};
/**
 * Try to parse the response body as JSON and return that instead of a
 * `Response` object.
 *
 * If the response code is 204 No Content, don't try to parse as json and
 * instead resolve with the original response.
 */
export const parseBodyAsJsonResponseTransformer = {
  then: function parseJsonBody(response) {
    if (response.status === 204) {
      return Promise.resolve(response);
    }
    return response.json().catch(jsonParseError => {
      throw makeJsonParseError(jsonParseError, response);
    });
  },
  catch: function parseJsonBody(error) {
    if (!error.response) return Promise.reject(error);
    return error.response
      .json()
      .catch(jsonParseError => {
        throw makeJsonParseError(jsonParseError, error.response);
      })
      .then(json => {
        error.json = json;
        throw error;
      });
  },
};
/**
 * Creates a request transformer that adds a URL prefix
 * @param base {string} base URL (eg, `https://plangrid-test.planfront.net`)
 * @param proxyPrefix {string} prefix to add for proxy
 * @param service {string} prefix to add for service
 * @returns {Function} request transformer
 */
export function buildUrlPrefixTransformer(base, proxyPrefix, service) {
  if (proxyPrefix.startsWith("/") || proxyPrefix.endsWith("/"))
    throw new Error("proxyPrefix should not start or end with a slash");
  if (service.startsWith("/") || service.endsWith("/"))
    throw new Error("service should not start or end with a slash");
  return function({ url, init }) {
    const fullUrl = new URL(url, base);
    // hack for /proxy/capi because istio doesn't do path rewriting on regex matching
    if (proxyPrefix !== "proxy" || service !== "capi") {
      fullUrl.pathname = `/${proxyPrefix}/${service}/${fullUrl.pathname.replace(
        /^\//,
        ""
      )}`;
    }
    return { url: fullUrl.href, init };
  };
}
export const addXRequestedByHeaderTransformer = buildRequestHeadersTransformer({
  "X-Requested-By": process.env.REACT_APP_NAME || "web-api-clients-unknown",
});
export const addXDestinationHandlerHeaderTransformer = buildRequestHeadersTransformer(
  {
    "X-Destination-Handler": "proxy",
  }
);
